import apiClient from "./apiClient";

// Track which invalid values we've already warned about
const warnedValues = new Set();

export const formatDuration = (seconds) => {
  // Guard against undefined, null, or non-numeric values
  if (seconds === undefined || seconds === null || isNaN(seconds)) {
    // Only log warning once per unique value
    const valueKey = seconds === null ? 'null' : seconds === undefined ? 'undefined' : 'NaN';
    if (!warnedValues.has(valueKey)) {
      console.warn('formatDuration received invalid seconds:', seconds);
      warnedValues.add(valueKey);
    }
    return '0m 0s';
  }

  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}m ${secs}s`;
}

/**
 * Get call duration in seconds. Uses analytics.duration when present and > 0,
 * otherwise derives from conversation first/last message timestamps, else 0.
 */
export const getCallDurationInSeconds = (call) => {
  const fromAnalytics = call?.analytics?.duration;
  if (typeof fromAnalytics === 'number' && fromAnalytics > 0) {
    return fromAnalytics;
  }
  const messages = call?.conversation;
  if (!Array.isArray(messages) || messages.length < 2) {
    return 0;
  }
  const firstTs = messages[0]?.timestamp;
  const lastTs = messages[messages.length - 1]?.timestamp;
  if (!firstTs || !lastTs) return 0;
  const firstMs = new Date(firstTs).getTime();
  const lastMs = new Date(lastTs).getTime();
  if (Number.isNaN(firstMs) || Number.isNaN(lastMs)) return 0;
  const seconds = (lastMs - firstMs) / 1000;
  return Math.max(0, seconds);
};

// Track which invalid timestamp values we've already warned about
const warnedTimestamps = new Set();

// Formatting Timestamp with timezone conversion
export const formatTimeStamp = (timestamp) => {
  // Guard against undefined or null timestamps
  if (!timestamp || typeof timestamp !== 'string') {
    // Only log warning once per unique value
    const valueKey = timestamp === null ? 'null' : timestamp === undefined ? 'undefined' : typeof timestamp;
    if (!warnedTimestamps.has(valueKey)) {
      console.warn('formatTimeStamp received invalid timestamp:', timestamp);
      warnedTimestamps.add(valueKey);
    }
    return 'Invalid Date';
  }

  try {
    // Parse the timestamp components
    const year = timestamp.substring(0, 4);
    const month = timestamp.substring(4, 6);
    const day = timestamp.substring(6, 8);
    const hour = timestamp.substring(9, 11) || '00'; // Adjusted to account for the hyphen
    const minute = timestamp.substring(11, 13) || '00';
    const second = timestamp.substring(13, 15) || '00';

    // Create date in UTC
    // This creates a date object from UTC components. When we call methods like
    // .getHours() or .getDate(), they will automatically use the user's local timezone.
    const localDate = new Date(Date.UTC(year, month - 1, day, hour, minute, second));

    // Format the date components using the user's local time
    const localHour = String(localDate.getHours()).padStart(2, '0');
    const localMinute = String(localDate.getMinutes()).padStart(2, '0');
    const localDay = String(localDate.getDate()).padStart(2, '0');
    const localMonth = String(localDate.getMonth() + 1).padStart(2, '0');
    const localYear = String(localDate.getFullYear()).slice(2);

    return `${localMonth}/${localDay}/${localYear} - ${localHour}:${localMinute}`;
  } catch (error) {
    // Only log error once per timestamp value
    if (!warnedTimestamps.has(timestamp)) {
      console.error('Error formatting timestamp:', timestamp, error);
      warnedTimestamps.add(timestamp);
    }
    return 'Invalid Date';
  }
}

// Extract success score from evaluation text
export const getSuccessScore = (evaluation) => {
  // First, check if evaluation exists and is a string.
  if (typeof evaluation !== 'string') {
    return null;
  }
  
  const match = evaluation.match(/success score[^\d]*(\d+)/i);
  return match ? parseInt(match[1], 10) : null; // Added radix 10 to parseInt, which is a best practice.
};

// Helper to determine if a score is considered good
export const isGoodScore = (score) => {
  return score >= 7;
};

// Get pass/fail status based on score
export const getScoreStatus = (score) => {
  return score >= 7 ? 'Pass' : score >= 4 ? 'Neutral' : 'Fail';
};

// Process call data to include success score
export const processCallData = (call) => {
  const score = getSuccessScore(call.evaluation);
  return {
    ...call,
    success_score: score,
    score_status: getScoreStatus(score),
    duration_formatted: formatDuration(getCallDurationInSeconds(call)),
    timestamp_formatted: formatTimeStamp(call.metadata?.timestamp),
  };
};

// export const sentimentCounts = {
//   positive: 0,
//   neutral: 0,
//   negative: 0,
// };

// export const sentimentCounter = (data) => {
//   // Reset counts first
//   sentimentCounts.positive = 0;
//   sentimentCounts.neutral = 0;
//   sentimentCounts.negative = 0;

//   data.forEach((call) => {
//     const trend = call.analytics.sentiment_analysis.trend;
//     if (trend === "positive") sentimentCounts.positive += 1;
//     else if (trend === "neutral") sentimentCounts.neutral += 1;
//     else if (trend === "negative") sentimentCounts.negative += 1;
//   });
// };

export const updateCallBookingResolved = async (customerId, callId, resolved) => {
    try {
        const response = await apiClient.patch(`/api/customers/${customerId}/calls/${callId}/`, {
            booking_resolved: resolved,
        });
        return response.data;
    } catch (error) {
        console.error(`Error updating call ${callId}:`, error);
        throw error;
    }
};
